import { useEffect, useRef, useState } from 'react';

function cardMediaSrc(card) {
  if (card.previewUrl) return card.previewUrl;
  if ((card.header_type || '').toUpperCase() === 'VIDEO' && card.video_base64) {
    return `data:video/mp4;base64,${card.video_base64}`;
  }
  if (card.image_base64) {
    return `data:image/jpeg;base64,${card.image_base64}`;
  }
  return null;
}

export function CarouselPreviewStrip({
  cards = [],
  parseBody,
  compact = false,
  carouselMediaFiles = {},
}) {
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [mediaUrls, setMediaUrls] = useState({});

  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - scrollerRef.current.offsetLeft;
    scrollLeft.current = scrollerRef.current.scrollLeft;
    scrollerRef.current.style.cursor = 'grabbing';
    // Remove CSS snapping while dragging for smooth movement
    scrollerRef.current.classList.remove('snap-x', 'snap-mandatory');
  };

  const handleMouseLeave = () => {
    if (isDragging.current) {
      isDragging.current = false;
      scrollerRef.current.style.cursor = 'grab';
      // Restore snapping when drag ends
      scrollerRef.current.classList.add('snap-x', 'snap-mandatory');
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    scrollerRef.current.style.cursor = 'grab';
    // Restore snapping when drag ends
    scrollerRef.current.classList.add('snap-x', 'snap-mandatory');
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - scrollerRef.current.offsetLeft;
    const walk = (x - startX.current) * 2;
    scrollerRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const prevCarouselMediaFiles = useRef(carouselMediaFiles);
  let stableCarouselMediaFiles = prevCarouselMediaFiles.current;

  if (stableCarouselMediaFiles !== carouselMediaFiles) {
    const prevKeys = Object.keys(stableCarouselMediaFiles || {});
    const nextKeys = Object.keys(carouselMediaFiles || {});
    let isSame = prevKeys.length === nextKeys.length;
    if (isSame) {
      for (const key of prevKeys) {
        if (stableCarouselMediaFiles[key] !== carouselMediaFiles[key]) {
          isSame = false;
          break;
        }
      }
    }
    if (!isSame) {
      prevCarouselMediaFiles.current = carouselMediaFiles;
      stableCarouselMediaFiles = carouselMediaFiles;
    }
  }

  useEffect(() => {
    const urls = {};
    Object.entries(stableCarouselMediaFiles).forEach(([index, file]) => {
      if (file) {
        urls[index] = URL.createObjectURL(file);
      }
    });
    setMediaUrls(urls);
    return () => {
      Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [stableCarouselMediaFiles]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;

    const onScroll = () => {
      const cardWidth = el.firstElementChild?.clientWidth || 1;
      const gap = 8;
      const index = Math.round(el.scrollLeft / (cardWidth + gap));
      setActiveIndex(
        Math.min(Math.max(index, 0), Math.max(cards.length - 1, 0)),
      );
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [cards.length]);

  const mediaHeight = compact ? 'h-20' : 'h-28';
  const cardWidth = compact ? 'w-[132px]' : 'w-[168px]';

  return (
    <div className="flex flex-col gap-1.5 w-full max-w-full">
      <div
        ref={scrollerRef}
        className="flex gap-2 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-0.5 cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onDragStart={(e) => e.preventDefault()}
      >
        {cards.map((card, index) => {
          const src = mediaUrls[index] || cardMediaSrc(card);
          const isVideo = carouselMediaFiles[index]
            ? carouselMediaFiles[index].type?.startsWith('video/')
            : (card.header_type || '').toUpperCase() === 'VIDEO';
          const bodyHtml =
            card.body_text && parseBody
              ? parseBody(card.body_text, index)
              : card.body_text || '';

          return (
            <div
              key={card.id || index}
              className={`${cardWidth} shrink-0 snap-start bg-white rounded-xl border border-[#E5E5E5] overflow-hidden divide-y divide-[#E5E5E5]`}
            >
              <div
                className={`${mediaHeight} bg-[#F0F2F5] flex items-center justify-center overflow-hidden`}
              >
                {src ? (
                  isVideo ? (
                    <video
                      src={src}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                    />
                  ) : (
                    <img
                      src={src}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )
                ) : (
                  <span className="text-nano text-[#8696A0] font-semibold uppercase">
                    {isVideo ? 'Video' : 'Image'}
                  </span>
                )}
              </div>
              <div className="p-2">
                <span className="text-nano text-[#111B21] leading-tight break-words whitespace-pre-wrap block">
                  {bodyHtml || (
                    <span className="text-[#8696A0] italic">Card text...</span>
                  )}
                </span>
              </div>
              {(card.buttons || []).map((btn) => (
                <button
                  key={btn.id || `${index}-${btn.type}-${btn.text}`}
                  type="button"
                  className="w-full h-6 flex items-center justify-center text-nano text-[#0088FF] font-bold select-none"
                >
                  {btn.text || 'Button text...'}
                </button>
              ))}
            </div>
          );
        })}
      </div>
      {cards.length > 1 && (
        <div className="flex items-center justify-center gap-1">
          {cards.map((card, index) => (
            <span
              key={card.id || index}
              className={`h-1.5 rounded-full transition-all ${
                index === activeIndex
                  ? 'w-3 bg-[#25D366]'
                  : 'w-1.5 bg-[#D1D7DB]'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
